import { ReactNode, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MAX_NOTICES, NoticeContext, NoticeStack } from '@shared/ui';
import type { Notice, NoticeAction, NoticeApi, NoticeKind } from '@shared/ui';

interface NoticeProviderProps {
  children: ReactNode;
}

/** Хранит окна ошибок и сообщений. Должен стоять внутри роутера: ошибки закрываются при смене маршрута. */
export function NoticeProvider({ children }: NoticeProviderProps) {
  const [notices, setNotices] = useState<Notice[]>([]);
  const nextId = useRef(1);
  const { pathname } = useLocation();

  const dismiss = useCallback((id: number) => {
    setNotices((list) => list.filter((n) => n.id !== id));
  }, []);

  const push = useCallback(
    (kind: NoticeKind, title: string, items: string[] = [], actions: NoticeAction[] = []) => {
      const id = nextId.current++;
      setNotices((list) => [{ id, kind, title, items, actions }, ...list].slice(0, MAX_NOTICES));
      return id;
    },
    [],
  );

  // Сообщения (например, «Отменить» после добавления) переживают переход, ошибки — нет.
  // Layout-эффект: обычный сработал бы после эффектов новой страницы и стёр её ошибку.
  useLayoutEffect(() => {
    setNotices((list) => (list.some((n) => n.kind === 'error') ? list.filter((n) => n.kind !== 'error') : list));
  }, [pathname]);

  const api = useMemo<NoticeApi>(
    () => ({
      error: (title, items, actions) => push('error', title, items, actions),
      info: (title, items, actions) => push('info', title, items, actions),
      dismiss,
    }),
    [push, dismiss],
  );

  return (
    <NoticeContext.Provider value={api}>
      {children}
      <NoticeStack notices={notices} onDismiss={dismiss} />
    </NoticeContext.Provider>
  );
}
